import dayjs from 'dayjs';
import { type TicketSla } from './_models';
import { slaHourCount } from './_utils';

const HOURS_PER_BUSINESS_DAY = 24;
const WORKDAY_START_HOUR = 8;
const WORKDAY_END_HOUR = 18;
const DUE_AT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

function isWeekend(date: dayjs.Dayjs): boolean {
  return date.day() === 0 || date.day() === 6;
}

function nextWorkdayStart(date: dayjs.Dayjs): dayjs.Dayjs {
  let next = date.add(1, 'day').hour(WORKDAY_START_HOUR).minute(0).second(0);
  while (isWeekend(next)) next = next.add(1, 'day');
  return next;
}

function workdayStartOnOrAfter(date: dayjs.Dayjs): dayjs.Dayjs {
  const todayStart = date.hour(WORKDAY_START_HOUR).minute(0).second(0);
  if (isWeekend(date) || date.hour() >= WORKDAY_END_HOUR) return nextWorkdayStart(date);
  return date.isBefore(todayStart) ? todayStart : date;
}

function addWorkingHours(from: Date, hours: number): dayjs.Dayjs {
  const fromDate = dayjs(from);
  let cursor = workdayStartOnOrAfter(fromDate);
  let remainingMinutes = hours * 60;
  for (;;) {
    const workdayEnd = cursor.hour(WORKDAY_END_HOUR).minute(0).second(0);
    const availableMinutes = workdayEnd.diff(cursor, 'minute');
    if (remainingMinutes <= availableMinutes) return cursor.add(remainingMinutes, 'minute');
    remainingMinutes -= availableMinutes;
    cursor = nextWorkdayStart(cursor);
  }
}

function addBusinessDays(from: Date, days: number): dayjs.Dayjs {
  let due = dayjs(from);
  let remainingDays = days;
  while (remainingDays > 0 || isWeekend(due)) {
    due = due.add(1, 'day');
    if (!isWeekend(due)) remainingDays -= 1;
  }
  return due;
}

function dueDate(sla: TicketSla, from: Date): dayjs.Dayjs {
  const hours = slaHourCount(sla);
  if (hours < HOURS_PER_BUSINESS_DAY) return addWorkingHours(from, hours);
  return addBusinessDays(from, hours / HOURS_PER_BUSINESS_DAY);
}

export function ticketDueAt(sla: TicketSla, from: Date): string {
  return dueDate(sla, from).format(DUE_AT_FORMAT);
}
